import { Button } from '@/components/ui/button';
import { formatNumber } from '@/utils/number';
import { pcmpSegmentClass } from '../../utils/pcmpSegmentClass';

interface PcmpQueueTabsProps<T extends string> {
  tabs: readonly T[];
  labels: Record<T, string>;
  value: T;
  /** Each tab's badge; left out while the counts load. */
  countOf?: ((tab: T) => number) | undefined;
  onChange: (tab: T) => void;
}

/** A queue's segmented tab strip with count badges. */
export function PcmpQueueTabs<T extends string>({
  tabs,
  labels,
  value,
  countOf,
  onChange,
}: PcmpQueueTabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label="Queue"
      className="flex w-full max-w-full items-center overflow-x-auto rounded-lg border bg-muted/40 p-1 sm:w-fit"
    >
      {tabs.map((tab) => (
        <Button
          key={tab}
          type="button"
          role="tab"
          aria-selected={value === tab}
          variant="ghost"
          size="sm"
          onClick={() => onChange(tab)}
          className={pcmpSegmentClass(value === tab)}
        >
          {labels[tab]}
          {countOf ? (
            <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">
              {formatNumber(countOf(tab))}
            </span>
          ) : null}
        </Button>
      ))}
    </div>
  );
}
